import { isAxiosError } from 'axios';
import * as React from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { TOASTER_TYPE, ToastMessage } from '../../../components';
import { usePayLinkCart } from '../context/PayLinkCartContext';
import { usePayLinkUser } from '../context/PaylinkUserContext';
import { enrollForLoyalty, getAllEnrolledLoyalty } from '../services/api';
import { ILoyaltyEnrolmentPayload } from '../types/walllet';

type TOpState =
  | "FetchingLoyalty"
  | "AlreadyEnrolled"
  | "EnrollingForLoyalty"
  | "EnrolledForLoyalty"
  | "FailedLoyalty";

type TUseLoyaltyEnrolmentArgs = {
  setIsEnrollmentDone: React.Dispatch<React.SetStateAction<boolean>>;
  enabled?: boolean;
};

const LOYALTY_ENROLL_DELAY_MS = 2000;
const ALREADY_ENROLLED_DELAY_MS = 3000;

const getEnrolledMembers = (response: any): any[] => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

export const useLoyaltyEnrolment = ({
  setIsEnrollmentDone,
  enabled = true,
}: TUseLoyaltyEnrolmentArgs) => {
  const queryClient = useQueryClient();
  const { user, shippingAddress } = usePayLinkUser();
  const { cartItems, storeProfile } = usePayLinkCart();
  const firstCartItem = cartItems?.[0];
  const timeoutRef = React.useRef<number | null>(null);
  const [operationState, setOperationState] =
    React.useState<TOpState>("FetchingLoyalty");

  const loyaltyPromotion = firstCartItem?.loyaltyPromotion;
  const enrolmentCustomerUniqueReferenceNumber =
    loyaltyPromotion?.customerUniqueReferenceNumber ??
    loyaltyPromotion?.merchantUniqueReferenceNumber ??
    firstCartItem?.customerUniqueReferenceNumber ??
    storeProfile?.customerUniqueReferenceNumber ??
    firstCartItem?.merchantUniqueReferenceNumber ??
    storeProfile?.merchantUniqueReferenceNumber;
  const enrolmentMerchantUniqueReferenceNumber =
    enrolmentCustomerUniqueReferenceNumber;

  const clearScheduledAction = React.useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  React.useEffect(() => clearScheduledAction, [clearScheduledAction]);

  const onEnrollForLoyalty = useMutation({
    mutationKey: ["PayLinkEnrollForLoyalty"],
    mutationFn: enrollForLoyalty,
    onSuccess: (data: any) => {
      if (data?.status === 200 || data?.status === 201) {
        queryClient.invalidateQueries({ queryKey: ["GetAccuredPoint"] });
        queryClient.invalidateQueries({ queryKey: ["GetEarnableRewards"] });
        queryClient.invalidateQueries({ queryKey: ["PayLinkApplicableRewards"] });
        queryClient.refetchQueries({ queryKey: ["PayLinkApplicableRewards"], type: "active" });
        setOperationState("EnrolledForLoyalty");
        clearScheduledAction();
        timeoutRef.current = window.setTimeout(() => {
          setIsEnrollmentDone(true);
        }, LOYALTY_ENROLL_DELAY_MS);
        return;
      }

      setOperationState("FailedLoyalty");
    },
    onError: (error: any) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        setOperationState("AlreadyEnrolled");
        clearScheduledAction();
        timeoutRef.current = window.setTimeout(() => {
          setIsEnrollmentDone(true);
        }, ALREADY_ENROLLED_DELAY_MS);
        return;
      }

      setOperationState("FailedLoyalty");
      ToastMessage(
        TOASTER_TYPE.ERROR,
        "Unable to enroll for loyalty right now. Please try again.",
      );
    },
  });

  const handleEnrollment = React.useCallback(() => {
    if (!user || !loyaltyPromotion || !enrolmentCustomerUniqueReferenceNumber) {
      setOperationState("FailedLoyalty");
      return;
    }

    const loyaltyPayload: ILoyaltyEnrolmentPayload = {
      walletUniqueReferenceNumber: user.walletUniqueReferenceNumber,
      consumerUniqueReferenceNumber: user.consumerUniqueReferenceNumber,
      customerUniqueReferenceNumber:
        enrolmentCustomerUniqueReferenceNumber,
      merchantUniqueReferenceNumber:
        enrolmentMerchantUniqueReferenceNumber,
      loyaltyProgrammeUniqueReferenceNumber:
        loyaltyPromotion.loyaltyProgrammeUniqueReferenceNumber,
      tierUniqueReferenceNumber: loyaltyPromotion.loyaltyTierReferenceNumber,
      tierName: loyaltyPromotion.loyaltyTierName,
      firstName: user.firstName ? user.firstName : "Kelvin",
      lastName: user.lastName ? user.lastName : "Hart",
      dateOfBirth: "2024-04-19T15:05:19.180Z",
      minimumPayout: loyaltyPromotion.minimumPayout ?? 0,
      pointsToValue: loyaltyPromotion.pointsToValue ?? 0,
      bonusPoints: loyaltyPromotion.bonusPoints ?? 0,
      attributes: [{ key: "", value: "" }],
      agreement: {
        termsAndConditions: true,
        dataUse: true,
      },
    };

    console.log({ user, loyaltyPayload });

    onEnrollForLoyalty.mutate(loyaltyPayload);
  }, [
    enrolmentCustomerUniqueReferenceNumber,
    enrolmentMerchantUniqueReferenceNumber,
    loyaltyPromotion,
    onEnrollForLoyalty,
    storeProfile?.merchantUniqueReferenceNumber,
    user,
  ]);

  const enrolledLoyaltyQuery = useQuery({
    queryKey: [
      "PayLinkAllEnrolledLoyalty",
      enrolmentCustomerUniqueReferenceNumber,
      enrolmentMerchantUniqueReferenceNumber,
      user?.consumerUniqueReferenceNumber,
    ],
    queryFn: () =>
      getAllEnrolledLoyalty({
        CustomerUniqueReferenceNumber:
          enrolmentCustomerUniqueReferenceNumber as string,
        // MerchantUniqueReferenceNumber: merchantUniqueReferenceNumber as string,
        ConsumerUniqueReferenceNumber:
          user?.consumerUniqueReferenceNumber as string,
        State: "Enrolled",
      }),
    enabled:
      enabled &&
      !!enrolmentCustomerUniqueReferenceNumber &&
      !!enrolmentMerchantUniqueReferenceNumber &&
      !!user?.consumerUniqueReferenceNumber &&
      !!loyaltyPromotion,
    refetchOnWindowFocus: false,
    retry: 3,
    retryDelay: 1000,
    staleTime: 0,
    onSuccess: (data: any) => {
      const enrolledMembers = getEnrolledMembers(data);

      if (enrolledMembers.length === 0) {
        setOperationState("EnrollingForLoyalty");
        clearScheduledAction();
        timeoutRef.current = window.setTimeout(
          () => handleEnrollment(),
          LOYALTY_ENROLL_DELAY_MS,
        );
        return;
      }

      queryClient.invalidateQueries({ queryKey: ["PayLinkApplicableRewards"] });
      queryClient.refetchQueries({ queryKey: ["PayLinkApplicableRewards"], type: "active" });
      setOperationState("AlreadyEnrolled");
      clearScheduledAction();
      timeoutRef.current = window.setTimeout(() => {
        setIsEnrollmentDone(true);
      }, ALREADY_ENROLLED_DELAY_MS);
    },
    onError: () => {
      setOperationState("FailedLoyalty");
    },
  });

  const handleProceedToCheckout = React.useCallback(() => {
    setIsEnrollmentDone(true);
  }, [setIsEnrollmentDone]);

  const isEnrolmentLoading =
    enrolledLoyaltyQuery.isLoading || onEnrollForLoyalty.isLoading;

  return {
    operationState,
    isEnrolmentLoading,
    isFetchingLoyalty: enrolledLoyaltyQuery.isLoading,
    isEnrollingForLoyalty: onEnrollForLoyalty.isLoading,
    handleEnrollment,
    handleProceedToCheckout,
    setOperationState,
    enrolledLoyaltyQuery,
    onEnrollForLoyalty,
    firstCartItem,
  };
};
